import { attrsFor, searchMentions } from './noteMentions';
import type { MentionItem, MentionSources } from './noteMentions';
import type { SupabaseClient } from '../../lib/supabase';

/**
 * The `@` popover, wired to Tiptap's suggestion plugin.
 *
 * Rendered with plain DOM rather than a React portal. The popover has to be
 * positioned against a ProseMirror coordinate and torn down the instant the
 * suggestion closes; going through React's tree for that means a render cycle
 * of lag on every keystroke, and a stale popover left behind when the editor
 * is destroyed mid-query.
 *
 * ── The race this is careful about ─────────────────────────────────────────
 *
 * Every keystroke starts a query. They come back out of order — "gi" can
 * resolve after "gift" — so a naive implementation flashes results for a
 * prefix the person has already finished typing, and Enter picks the wrong
 * record. Each query records the term it was for and drops itself if the term
 * has moved on.
 */

export interface SuggestionRenderer<T = MentionItem> {
  onStart: (props: SuggestionProps<T>) => void;
  onUpdate: (props: SuggestionProps<T>) => void;
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
  onExit: () => void;
}

export interface SuggestionProps<T = MentionItem> {
  items: T[];
  command: (attrs: Record<string, unknown>) => void;
  clientRect?: (() => DOMRect | null) | null;
}

/**
 * The least a row needs to be drawable.
 *
 * `group` is optional and drives a heading above the first row of each run.
 * That is how one popover carries two scopes — People and Records — without
 * two popovers, two key handlers and two places for the highlight to get out
 * of step.
 */
export interface SuggestionRow {
  id: string;
  label: string;
  hint: string;
  group?: string;
}

/** Exported for testing: which item a key press moves to. */
export function nextIndex(current: number, length: number, key: string): number {
  if (length === 0) return 0;
  if (key === 'ArrowDown') return (current + 1) % length;
  if (key === 'ArrowUp') return (current - 1 + length) % length;
  return current;
}

/** Exported for testing: whether this key is one the popover consumes. */
export function popoverHandlesKey(key: string): boolean {
  // Enter and Tab both accept. Escape closes. Arrows move. Everything else
  // belongs to the editor — swallowing it would stop somebody typing.
  return ['ArrowDown', 'ArrowUp', 'Enter', 'Tab', 'Escape'].includes(key);
}

/**
 * A `@` popover over any list of rows.
 *
 * Split out from mentionSuggestion so the comment composer can search two
 * scopes — people AND records — through the same popover, the same key
 * handling and the same out-of-order guard. A second copy of this file would
 * have been the obvious move and the wrong one: the race below is subtle, and
 * a copy of it drifts.
 */
export interface SuggestionConfig<T extends SuggestionRow> {
  search: (query: string) => Promise<T[]>;
  attrs: (item: T) => Record<string, unknown>;
  ariaLabel: string;
  /** Characters before the first query. Two by default. */
  minChars?: number;
}

export function buildSuggestion<T extends SuggestionRow>(config: SuggestionConfig<T>) {
  let latestTerm = '';
  const minChars = config.minChars ?? 2;

  return {
    char: '@',
    // Two characters before asking. One character matches most of the
    // database and the list is noise.
    allowSpaces: false,

    items: async ({ query }: { query: string }): Promise<T[]> => {
      latestTerm = query;
      if (query.trim().length < minChars) {
        return [];
      }
      const results = await config.search(query);
      // Out-of-order guard: if the person kept typing, this answer is for a
      // prefix they have already moved past.
      if (query !== latestTerm) {
        return [];
      }
      return results;
    },

    render: (): SuggestionRenderer<T> => {
      let element: HTMLDivElement | null = null;
      let items: T[] = [];
      let selected = 0;
      let commandFn: ((attrs: Record<string, unknown>) => void) | null = null;

      function draw(): void {
        if (element === null) return;
        element.innerHTML = '';
        if (items.length === 0) {
          const empty = document.createElement('p');
          empty.className = 'note-mention-empty';
          empty.textContent = 'Nothing matches';
          element.append(empty);
          return;
        }
        let lastGroup: string | undefined;
        items.forEach((item, index) => {
          // A heading above the first row of each run, so People and Records
          // read as two scopes rather than one jumbled list. Not focusable and
          // not counted: the index the arrow keys move through is the ITEM
          // index, and a heading in that sequence would be a row Enter cannot
          // pick.
          if (item.group !== undefined && item.group !== lastGroup) {
            lastGroup = item.group;
            const heading = document.createElement('p');
            heading.className = 'note-mention-group';
            heading.textContent = item.group;
            element?.append(heading);
          }

          const button = document.createElement('button');
          button.type = 'button';
          button.className = `note-mention-option${index === selected ? ' note-mention-option--on' : ''}`;
          button.setAttribute('role', 'option');
          button.setAttribute('aria-selected', String(index === selected));

          const label = document.createElement('span');
          label.className = 'note-mention-option-label';
          label.textContent = item.label;

          const hint = document.createElement('span');
          hint.className = 'note-mention-option-hint';
          hint.textContent = item.hint;

          button.append(label, hint);
          button.addEventListener('mousedown', (event) => {
            // mousedown, not click: click fires after the editor has already
            // lost the selection, and the mention lands nowhere.
            event.preventDefault();
            pick(index);
          });
          element?.append(button);
        });
      }

      function pick(index: number): void {
        const item = items[index];
        if (item === undefined || commandFn === null) return;
        commandFn(config.attrs(item));
      }

      function place(rect: DOMRect | null): void {
        if (element === null || rect === null) return;
        element.style.top = `${String(rect.bottom + window.scrollY + 4)}px`;
        element.style.left = `${String(rect.left + window.scrollX)}px`;
      }

      return {
        onStart: (props) => {
          element = document.createElement('div');
          element.className = 'note-mention-popover';
          element.setAttribute('role', 'listbox');
          element.setAttribute('aria-label', config.ariaLabel);
          document.body.append(element);
          items = props.items;
          selected = 0;
          commandFn = props.command;
          draw();
          place(props.clientRect?.() ?? null);
        },
        onUpdate: (props) => {
          items = props.items;
          // Reset rather than clamp: the list is now a different list, and
          // keeping the index would leave the highlight on an unrelated row.
          selected = 0;
          commandFn = props.command;
          draw();
          place(props.clientRect?.() ?? null);
        },
        onKeyDown: ({ event }) => {
          if (!popoverHandlesKey(event.key)) {
            return false;
          }
          if (event.key === 'Escape') {
            element?.remove();
            element = null;
            return true;
          }
          if (event.key === 'Enter' || event.key === 'Tab') {
            pick(selected);
            return true;
          }
          selected = nextIndex(selected, items.length, event.key);
          draw();
          return true;
        },
        onExit: () => {
          element?.remove();
          element = null;
          commandFn = null;
        },
      };
    },
  };
}

/** The notes editor's `@`: projects, tasks and CG orders. */
export function mentionSuggestion(client: SupabaseClient, sources: MentionSources) {
  return buildSuggestion<MentionItem>({
    search: (query) => searchMentions(client, query, sources),
    attrs: (item) => attrsFor(item) as unknown as Record<string, unknown>,
    ariaLabel: 'Mention a project, task or order',
  });
}
